/**
 * Alert — inline status callout.
 *
 * Shadcn-style: variants for info / warn / bad / ok with matching tones
 * pulled from `@flint/ui/tokens.css` (no fresh palette). Used for:
 *
 *   - server-side error messages on forms (replaces ad-hoc `<p className="text-bad">`)
 *   - first-time-user nudges on empty states
 *   - "this is a stub for V1.x" notes on partially-wired surfaces
 *
 * For "are you sure?" interactions use `ConfirmDialog` instead — Alert is
 * a passive surface, not an action prompt.
 */
import * as React from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn.js";

export type AlertIntent = "info" | "warn" | "bad" | "ok";

const INTENT_STYLES: Record<AlertIntent, { wrap: string; icon: string }> = {
  info: { wrap: "bg-accent-subtle border-accent-subtle text-accent-hover", icon: "text-accent" },
  // AC91: text-warn on bg-warn-50 fails WCAG contrast. Bumped to amber-800.
  warn: { wrap: "bg-warn-50 border-warn-100 text-amber-800", icon: "text-warn" },
  bad: { wrap: "bg-bad-50 border-bad-100 text-bad", icon: "text-bad" },
  ok: { wrap: "bg-ok-50 border-ok-100 text-ok", icon: "text-ok" },
};

const INTENT_ICONS: Record<AlertIntent, LucideIcon> = {
  info: Info,
  warn: TriangleAlert,
  bad: CircleAlert,
  ok: CircleCheck,
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  intent?: AlertIntent;
  /** Optional bold heading. Pair with `description` for the standard shape. */
  title?: React.ReactNode;
  /** Body text. Renders alone when `title` is omitted. */
  description?: React.ReactNode;
  /** Override the default Lucide icon (e.g. a domain-specific symbol). */
  icon?: LucideIcon | null;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { className, intent = "info", title, description, icon, children, ...props },
  ref,
) {
  const styles = INTENT_STYLES[intent];
  const Icon = icon === null ? null : (icon ?? INTENT_ICONS[intent]);

  return (
    <div
      ref={ref}
      role={intent === "bad" || intent === "warn" ? "alert" : "status"}
      className={cn("flex items-start gap-3 p-3 border rounded-md", styles.wrap, className)}
      {...props}
    >
      {Icon ? (
        <span aria-hidden className={cn("inline-flex shrink-0 mt-0.5", styles.icon)}>
          <Icon size={16} strokeWidth={2} />
        </span>
      ) : null}
      <div className="flex-1 min-w-0 text-sm leading-relaxed">
        {title ? <p className="font-semibold mb-0.5">{title}</p> : null}
        {description ? <p className="opacity-90">{description}</p> : null}
        {children}
      </div>
    </div>
  );
});
